import { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { useLyraIntl } from '@lyrahealth-inc/shared-app-logic/src/features/intl/hooks/useLyraIntl'
import { useManagerCoachingSeatEligibility as useManagerCoachingSeatEligibilityShared } from '@lyrahealth-inc/shared-app-logic/src/features/managerHub/hooks/useManagerCoachingSeatEligibility'
import {
  type ManagerCoachingSeatState,
  type UseManagerCoachingSeatEligibilityOptions,
} from '@lyrahealth-inc/shared-app-logic/src/features/managerHub/types'
import { shouldFetchManagerCoachingSeatEligibility } from '@lyrahealth-inc/shared-app-logic/src/features/managerHub/utils'
import { ProductTypeContext } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/productType/productTypeContext'

import { useAppDispatch } from '../../data/storeConfiguration/store'
import { getSeatBasedLicenses } from '../../data/user/userActions'
import {
  getId,
  getIsUserInternational,
  getIsUserLoggedIn,
  getIsUserMinor,
  getManagerCoachingSeatEligibility,
  getUserDisplayLanguageOrDefault,
  getUserSeatBasedLicenses,
  getUserSeatBasedLicensesError,
} from '../../data/user/userSelectors'

/** Select legacy app context and retain Redux seat-request orchestration. */
export const useManagerCoachingSeatEligibility = ({
  shouldFetch = true,
}: UseManagerCoachingSeatEligibilityOptions = {}): ManagerCoachingSeatState => {
  const dispatch = useAppDispatch()
  const { isManagerHubEnabled } = useFlags()
  const { productType } = useContext(ProductTypeContext)
  const { activeLanguage } = useLyraIntl()
  const isInternationalUser = useSelector(getIsUserInternational)
  const isUserLoggedIn = useSelector(getIsUserLoggedIn)
  const isUserMinor = useSelector(getIsUserMinor)
  const lyraId = useSelector(getId)
  const userDisplayLanguage = useSelector(getUserDisplayLanguageOrDefault)
  const displayLanguage = isUserLoggedIn ? userDisplayLanguage : activeLanguage
  const seatBasedLicenses = useSelector(getUserSeatBasedLicenses)
  const seatBasedLicensesError = useSelector(getUserSeatBasedLicensesError)
  const seatEligibility = useSelector(getManagerCoachingSeatEligibility)

  const shouldFetchSeatEligibility = shouldFetchManagerCoachingSeatEligibility({
    isManagerHubEnabled,
    productType,
    isUserMinor,
    language: displayLanguage,
    isInternationalUser,
    isUserLoggedIn,
    lyraId,
    shouldFetch,
  })

  useEffect(() => {
    if (!shouldFetchSeatEligibility || !lyraId) {
      return
    }
    if (seatBasedLicenses === undefined && !seatBasedLicensesError) {
      dispatch(getSeatBasedLicenses(lyraId))
    }
  }, [dispatch, lyraId, seatBasedLicenses, seatBasedLicensesError, shouldFetchSeatEligibility])

  return useManagerCoachingSeatEligibilityShared({ shouldFetchSeatEligibility, seatEligibility })
}
